import * as React from "react";
import { format, subDays, startOfDay, endOfDay, startOfMonth, startOfYear } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";

import { cn } from "../lib/utils";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

const DEFAULT_PRESETS = [
  {
    label: "Today",
    value: "today",
    getRange: () => {
      const now = new Date();
      return { from: startOfDay(now), to: endOfDay(now) };
    },
  },
  {
    label: "Last 7 days",
    value: "last7",
    getRange: () => {
      const now = new Date();
      return { from: startOfDay(subDays(now, 6)), to: endOfDay(now) };
    },
  },
  {
    label: "Last 30 days",
    value: "last30",
    getRange: () => {
      const now = new Date();
      return { from: startOfDay(subDays(now, 29)), to: endOfDay(now) };
    },
  },
  {
    label: "Month to date",
    value: "mtd",
    getRange: () => {
      const now = new Date();
      return { from: startOfMonth(now), to: endOfDay(now) };
    },
  },
  {
    label: "Year to date",
    value: "ytd",
    getRange: () => {
      const now = new Date();
      return { from: startOfYear(now), to: endOfDay(now) };
    },
  },
];

const DateRangePicker = React.forwardRef(
  (
    {
      value: controlledValue,
      defaultValue,
      onChange,
      presets = true,
      customPresets,
      presetLabels,
      placeholder = "Select date range",
      clearLabel = "Clear date range",
      locale,
      disabledDates,
      align = "start",
      className,
      ...props
    },
    ref
  ) => {
    const [uncontrolledValue, setUncontrolledValue] = React.useState(
      defaultValue || { from: undefined, to: undefined }
    );
    const [open, setOpen] = React.useState(false);
    const [activePreset, setActivePreset] = React.useState(null);

    const isControlled = controlledValue !== undefined;
    const value = isControlled ? controlledValue : uncontrolledValue;

    const activePresetsList = React.useMemo(() => {
      if (customPresets && Array.isArray(customPresets)) return customPresets;
      if (presets === true) {
        return DEFAULT_PRESETS.map((preset) => ({
          ...preset,
          label: presetLabels?.[preset.value] || preset.label,
        }));
      }
      return [];
    }, [presets, customPresets, presetLabels]);

    const handleSelect = React.useCallback(
      (newRange) => {
        setActivePreset(null);
        if (!isControlled) {
          setUncontrolledValue(newRange);
        }
        onChange?.(newRange);
      },
      [isControlled, onChange]
    );

    const handlePresetClick = React.useCallback(
      (preset) => {
        const range = preset.getRange();
        setActivePreset(preset.value);
        if (!isControlled) {
          setUncontrolledValue(range);
        }
        onChange?.(range);
      },
      [isControlled, onChange]
    );

    const handleClear = React.useCallback(
      (e) => {
        e.stopPropagation();
        setActivePreset(null);
        const emptyRange = { from: undefined, to: undefined };
        if (!isControlled) {
          setUncontrolledValue(emptyRange);
        }
        onChange?.(emptyRange);
      },
      [isControlled, onChange]
    );

    const formattedLabel = React.useMemo(() => {
      const formatOpts = locale ? { locale } : undefined;
      if (value?.from) {
        if (value.to) {
          return `${format(value.from, "LLL dd, y", formatOpts)} – ${format(value.to, "LLL dd, y", formatOpts)}`;
        }
        return format(value.from, "LLL dd, y", formatOpts);
      }
      return placeholder;
    }, [value, placeholder, locale]);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <div className="relative inline-flex items-center">
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              variant="outline"
              data-testid="date-range-picker-trigger"
              className={cn(
                "h-10 justify-start text-left font-normal bg-background border-border hover:bg-muted/60 text-foreground rounded-xl min-w-[240px] pr-8 cursor-pointer focus-visible:ring-2 focus-visible:ring-ring",
                !value?.from && "text-muted-foreground",
                className
              )}
              {...props}
            >
              <CalendarIcon className="mr-2 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="truncate flex-1">{formattedLabel}</span>
            </Button>
          </PopoverTrigger>
          {value?.from && (
            <span
              role="button"
              tabIndex={0}
              data-testid="date-range-picker-clear"
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleClear(e);
                }
              }}
              aria-label={clearLabel}
              className="absolute right-2 z-10 flex h-7 w-7 items-center justify-center rounded-full p-1 hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-3.5" aria-hidden="true" />
              <span className="sr-only">{clearLabel}</span>
            </span>
          )}
        </div>
        <PopoverContent
          align={align}
          data-testid="date-range-picker-popover"
          className="w-auto p-0 bg-popover text-popover-foreground border border-border shadow-2xl rounded-2xl overflow-hidden z-50"
        >
          <div className="flex flex-col sm:flex-row">
            {/* Quick Presets Sidebar / Top Bar */}
            {activePresetsList.length > 0 && (
              <div className="flex flex-row sm:flex-col gap-1 p-2.5 border-b sm:border-b-0 sm:border-r border-border overflow-x-auto sm:overflow-x-visible sm:w-40 shrink-0 bg-muted/30">
                {activePresetsList.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    data-testid={`date-range-preset-${preset.value}`}
                    onClick={() => handlePresetClick(preset)}
                    className={cn(
                      "px-2.5 py-2 text-xs text-left rounded-lg transition-colors whitespace-nowrap cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                      activePreset === preset.value
                        ? "bg-primary/15 text-primary font-semibold border border-primary/30"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            )}

            {/* Calendar */}
            <div className="relative p-2">
              <Calendar
                mode="range"
                selected={value}
                onSelect={handleSelect}
                numberOfMonths={1}
                disabled={disabledDates}
                locale={locale}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);
DateRangePicker.displayName = "DateRangePicker";

export { DateRangePicker, DEFAULT_PRESETS };
